import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import ModalWrapper from 'components/ModalWrapper';
import RoleService from 'api/services/RoleService';
import PropTypes from 'prop-types';

function TeamUserFormModal({ open, onClose, onSubmit, user }) {
    const { t } = useTranslation();
    const isEdit = !!user;
    const [roles, setRoles] = useState([]);

    const { register, handleSubmit, reset, formState: { errors, isSubmitting } } = useForm({
        defaultValues: { name: '', email: '', password: '', role: '' }
    });

    // Load roles when modal opens
    useEffect(() => {
        if (open) {
            RoleService.getRoles()
                .then(data => setRoles(data.results ?? []))
                .catch(() => {});

            reset({
                name: user?.name ?? '',
                email: user?.email ?? '',
                password: '',
                role: user?.role?.id ?? '',
            });
        }
    }, [open, user, reset]);

    const onFormSubmit = async (data) => {
        const payload = { name: data.name, email: data.email, role: data.role };
        // Only include password if provided (create: always, edit: only when filled)
        if (data.password) {
            payload.password = data.password;
        }
        await onSubmit(payload);
        onClose();
    };

    return (
        <ModalWrapper
            open={open}
            setOpen={onClose}
            title={isEdit ? t('team_users.edit_user') : t('team_users.add_user')}
        >
            <form onSubmit={handleSubmit(onFormSubmit)} className="w-96 space-y-4">

                {/* Name */}
                <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                        {t('team_users.name')}
                    </label>
                    <input
                        type="text"
                        className="w-full border border-gray-300 rounded px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                        {...register('name', { required: t('team_users.name_required') })}
                    />
                    {errors.name && <p className="text-red-500 text-xs mt-1">{errors.name.message}</p>}
                </div>

                {/* Email */}
                <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                        {t('team_users.email')}
                    </label>
                    <input
                        type="email"
                        className="w-full border border-gray-300 rounded px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                        {...register('email', {
                            required: t('team_users.email_required'),
                            pattern: {
                                value: /^[^\s@]+@(?:[^\s@.]+\.)+[^\s@.]+$/,
                                message: t('team_users.email_invalid'),
                            }
                        })}
                    />
                    {errors.email && <p className="text-red-500 text-xs mt-1">{errors.email.message}</p>}
                </div>

                {/* Password */}
                <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                        {t('team_users.password')}
                    </label>
                    <input
                        type="password"
                        autoComplete="new-password"
                        className="w-full border border-gray-300 rounded px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                        {...register('password', {
                            required: isEdit ? false : t('team_users.password_required'),
                            validate: value => {
                                if (!value) return; // empty on edit = keep current
                                if (value.length < 8) return t('team_users.password_invalid');
                                if (!/[a-zA-Z]/.test(value) || !/\d/.test(value)) return t('team_users.password_invalid');
                            }
                        })}
                    />
                    {isEdit && (
                        <p className="text-gray-400 text-xs mt-1">{t('team_users.password_hint_edit')}</p>
                    )}
                    {errors.password && <p className="text-red-500 text-xs mt-1">{errors.password.message}</p>}
                </div>

                {/* Role */}
                <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                        {t('team_users.role')}
                    </label>
                    <select
                        className="w-full border border-gray-300 rounded px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white"
                        {...register('role', { required: t('team_users.role_required') })}
                    >
                        <option value="">— {t('team_users.role')} —</option>
                        {roles.map(r => (
                            <option key={r.id} value={r.id}>{r.name}</option>
                        ))}
                    </select>
                    {errors.role && <p className="text-red-500 text-xs mt-1">{errors.role.message}</p>}
                </div>

                {/* Actions */}
                <div className="flex justify-end gap-2 pt-2">
                    <button
                        type="button"
                        onClick={onClose}
                        className="px-4 py-2 text-sm rounded border border-gray-300 text-gray-700 hover:bg-gray-50"
                    >
                        {t('actions.cancel')}
                    </button>
                    <button
                        type="submit"
                        disabled={isSubmitting}
                        className="px-4 py-2 text-sm rounded bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-50"
                    >
                        {isEdit ? t('actions.update') : t('actions.create')}
                    </button>
                </div>
            </form>
        </ModalWrapper>
    );
}

TeamUserFormModal.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onSubmit: PropTypes.func.isRequired,
  user: PropTypes.object,
};

export default TeamUserFormModal;
